import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
  Button,
  Dialog,
  DialogHost,
  DialogTitle,
  Drawer,
  DrawerHeader,
  DrawerPanel,
  DrawerPopup,
  DrawerTitle,
  EditableTitleField,
  Frame,
  FrameHeader,
  FramePanel,
  Icon,
  Menu,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
  Separator,
  Tabs,
  TabsList,
  TabsPanel,
  TabsTab,
  Text,
  Toggle,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useElementSize,
  useIsMobile,
  useWidgetRegistry,
  useWidgetTitleContext,
  Widget,
  WidgetInstanceProvider,
  WidgetSettingsPanel,
  WidgetSurfaceProvider,
  widgetTitle,
  type IconName,
  type SurfaceToken,
  type WidgetConfig,
  type WidgetDetailPresentation,
  type WidgetInstance,
  type WidgetModule,
} from "@salestrak/ui";
import { WidgetRefreshIcon, WidgetRefreshStatus } from "./dashboard-refresh-control";
import { useWidgetRefresh, WidgetRefreshBoundary } from "./use-widget-refresh";
import { WidgetDataPanel } from "./widget-data-panel";
import type { DetailViewMode } from "./widget-detail-view-mode";
// Relative import (not the barrel) — the barrel re-exports this dialog.
import {
  useWidgetDrilldown,
  type WidgetDataSlice,
  WidgetDrilldownProvider,
} from "./widget-drilldown";
import type { Omittable } from "@salestrak/ui";
import { DetailStackHost } from "#app/components/detail-view/detail-stack";
import { FULLSCREEN_DIALOG_POPUP_CLASS } from "#app/components/detail-view/detail-view-popup";
import { IssueViewDialogBoundaryPopup } from "#app/components/issue-view";
import { AnalyticsContext, WidgetViewEvent, trackEvent } from "#app/lib/analytics";

// One header button cycles the detail view; each mode's icon shows where you ARE,
// the tooltip says where a press goes.
type ViewModeControl = { icon: IconName; next: DetailViewMode; label: string };

const VIEW_MODE_CYCLE: Record<DetailViewMode, ViewModeControl> = {
  chart: { icon: "bar-chart", next: "data", label: "Chart view - click for data only" },
  split: { icon: "columns", next: "chart", label: "Chart + data - click for chart only" },
  data: { icon: "table-properties", next: "split", label: "Data view - click for chart + data" },
};

// A `"chart"` presentation has no Data view: the drawer holds Settings alone,
// so the button is a two-state switch between the body and the body beside
// its settings. `data` is unreachable here and maps back onto the split.
const SETTINGS_ONLY_CYCLE: Record<DetailViewMode, ViewModeControl> = {
  chart: { icon: "bar-chart", next: "split", label: "Chart view - click for settings" },
  split: { icon: "columns", next: "chart", label: "Chart + settings - click for chart only" },
  data: { icon: "columns", next: "chart", label: "Chart + settings - click for chart only" },
};

/** The presentation a def declares for one instance — the declaration is a
 *  value or a read of the resolved config. */
function resolveDetailPresentation(
  def: WidgetModule | undefined,
  config: WidgetConfig,
): WidgetDetailPresentation | undefined {
  const declared = def?.detailPresentation;
  return typeof declared === "function" ? declared(config) : declared;
}

type WidgetDetailDialogProps = Omittable<{
  instance: WidgetInstance | null;
  open: boolean;
  canManage: boolean;
  onOpenChange: (open: boolean) => void;
  /** Navigate to the previous widget; omit/undefined to disable (at the start). */
  onPrevious?: () => void;
  /** Navigate to the next widget; omit/undefined to disable (at the end). */
  onNext?: () => void;
  onDuplicate: (instance: WidgetInstance) => void;
  /** Drop a curated instance's preset lock. Rendered only while the instance
   *  is locked; omit when the viewer may not edit the board. */
  onConvertToCustom?: (instance: WidgetInstance) => void;
  onDelete: (id: string) => void;
  /** Persist a config patch — the header breadcrumb rename commits `{ title }`. */
  onConfigChange: (id: string, patch: WidgetConfig) => void;
  /** Chart-element drill-down: when set, the drawer opens on the Data tab
   *  filtered to this slice. Owned by the Dashboard host. */
  dataSlice?: WidgetDataSlice | null;
  /** Monotonic id bumped by the host on every drilldown open — presents the
   *  Data panel alone (no chart) even for an unfiltered open (null slice).
   *  Unchanged by plain Maximize. */
  dataRequestId?: number;
  /** Replace or clear the host's slice. The dialog calls it with a slice when
   *  a chart element inside the open dialog is clicked and with `null` when the
   *  Data panel's slice chip is dismissed. */
  onDataSliceChange?: (slice: WidgetDataSlice | null) => void;
  /** Monotonic id bumped by the host when the gear is pressed — opens the drawer
   *  on the Settings tab (the gear's whole purpose). Unchanged by plain Maximize. */
  settingsRequestId?: number;
  /** Monotonic id bumped by the host when a widget body asks for the chart-only
   *  presentation — the body fills the dialog with no drawer. Unchanged by
   *  plain Maximize. */
  chartRequestId?: number;
}>;

/**
 * Full-screen widget editor opened by maximizing a widget. A large preview of
 * the widget can fill the body, split with a Settings / Data tabbed drawer, or
 * give the drawer the full body width. Settings is manage-gated, so non-managers
 * see a Data-only drawer.
 *
 * Layout is a `Frame` filling the dialog: the header controls sit directly on
 * the tray (`FrameHeader`), and the preview + drawer region is one
 * `FramePanel`. The panel is a `sunken` well — the tray floats at `popover`,
 * so its panels recess rather than stack more chrome on top.
 */
export function WidgetDetailDialog({
  instance,
  open,
  canManage,
  onOpenChange,
  onPrevious,
  onNext,
  onDuplicate,
  onConvertToCustom,
  onDelete,
  onConfigChange,
  dataSlice,
  dataRequestId,
  onDataSliceChange,
  settingsRequestId,
  chartRequestId,
}: WidgetDetailDialogProps) {
  const registry = useWidgetRegistry();
  const titleContext = useWidgetTitleContext();
  const isMobile = useIsMobile();
  // Preset locks: a curated instance keeps its locked settings fixed until it
  // is converted to custom (the menu item below). Resolved config, so a seeded
  // row infers its preset from the same values the card reads.
  const def = instance ? registry.get(instance.type) : undefined;
  const resolvedConfig = instance
    ? registry.resolveConfig(def, instance.config, instance.schemaVersion ?? 1)
    : null;
  const lockedKeys = instance
    ? registry.lockedKeys(instance.type, resolvedConfig)
    : registry.lockedKeys("", null);
  const presentation = resolvedConfig ? resolveDetailPresentation(def, resolvedConfig) : undefined;
  // A def declaring `detailPresentation: "data"` (tables) maximizes its home card
  // straight to the data view — replaces a per-type hardcode that skipped the
  // visually identical favorites-list home card (D13).
  const directDataFullscreen = presentation === "data" && instance?.config?.home === true;
  // A `"chart"` presentation discloses its rows in the body itself, so the
  // drawer has no Data tab — Settings is all it holds, and a viewer who cannot
  // edit has no drawer at all: the body is the whole dialog.
  const hasDataTab = presentation !== "chart";
  const drawerless = !hasDataTab && !canManage;
  const [viewMode, setViewMode] = useState<DetailViewMode>("chart");
  const [panelTab, setPanelTab] = useState("settings");
  // Phones never render the desktop split. If the viewport changes while an
  // open desktop dialog is split, immediately present its drawer as one pane.
  const visibleViewMode: DetailViewMode = drawerless
    ? "chart"
    : isMobile && viewMode === "split"
      ? "data"
      : viewMode;
  const viewModeControl: ViewModeControl = isMobile
    ? visibleViewMode === "chart"
      ? {
          icon: "bar-chart",
          next: "data",
          label: hasDataTab
            ? "Chart view - click for settings and data"
            : "Chart view - click for settings",
        }
      : {
          icon: "table-properties",
          next: "chart",
          label: hasDataTab ? "Settings and data - click for chart" : "Settings - click for chart",
        }
    : (hasDataTab ? VIEW_MODE_CYCLE : SETTINGS_ONLY_CYCLE)[visibleViewMode];
  // Drawer width is user-draggable from the drawer's own left edge; capped so
  // the preview always keeps some room. The cap is
  // measured off the body panel the drawer lives in (measure what you size),
  // not the window.
  const [panelWidth, setPanelWidth] = useState(420);
  const { ref: bodyRef, size: bodySize } = useElementSize<HTMLDivElement>();
  const panelMaxWidth = Math.max(640, Math.floor(bodySize.width * 0.85));

  // Each open starts fresh from its entry point: plain Fullscreen shows the
  // widget beside its Data panel (Data fills on phones, which have no split),
  // and the request-id effects below (declared after, so they win the same
  // commit) specialize a drilldown/gear open. Nothing carries across opens.
  // Without a Data tab there is nothing to open beside: the body stands alone
  // until a gear press asks for Settings. A `"data"` presentation (a table, a
  // lone figure) opens on its Data view alone: beside it the body would only
  // restate the rows, or a number the card already showed.
  const opensOnData = presentation === "data";
  useEffect(() => {
    if (!open) return;
    if (hasDataTab) {
      setViewMode(isMobile || opensOnData ? "data" : "split");
      setPanelTab("data");
    } else {
      setViewMode("chart");
      setPanelTab("settings");
    }
  }, [hasDataTab, isMobile, open, opensOnData]);

  // A board drilldown opens the Data panel alone: the user asked for the
  // rows behind the chart, so the chart gives the drawer the whole body. Keyed
  // on the host's monotonic request id, not the slice, so an *unfiltered* open
  // (null slice, e.g. the metric widget) still surfaces Data. Only board
  // clicks bump the id — a click inside the open dialog goes through
  // `inDialogDrilldown` below and never reaches the host's counter.
  useEffect(() => {
    if (dataRequestId) {
      setPanelTab("data");
      setViewMode("data");
    }
  }, [dataRequestId]);

  // Clicks on the chart rendered INSIDE this dialog re-filter the Data panel
  // in place. The layout the user chose stays: a split stays split, Data-only
  // stays Data-only, and only the chart-only presentation opens the drawer
  // (as split, so the chart keeps its place) because a slice with no Data
  // panel would be invisible. `openChart` still routes to the board host — the
  // body is already at dialog size, and that presentation is the host's call.
  const hostDrilldown = useWidgetDrilldown();
  const inDialogDrilldown = useMemo(
    () => ({
      openData: (_instanceId: string, slice: WidgetDataSlice | null) => {
        onDataSliceChange?.(slice);
        setPanelTab("data");
        setViewMode((mode) => (mode === "chart" ? (isMobile ? "data" : "split") : mode));
      },
      openChart: (instanceId: string) => hostDrilldown?.openChart(instanceId),
    }),
    [hostDrilldown, isMobile, onDataSliceChange],
  );

  // A gear open derives the drawer from its entry point: split view on the
  // Settings tab. Keyed on the host's monotonic request id; a plain Maximize
  // leaves the id untouched.
  useEffect(() => {
    if (settingsRequestId) {
      setPanelTab("settings");
      setViewMode(isMobile ? "data" : "split");
    }
  }, [isMobile, settingsRequestId]);

  // A chart-only open gives the body the whole dialog: the widget asked to be
  // seen at dialog size, not for its drawer. Keyed on the host's monotonic
  // request id like the two above.
  useEffect(() => {
    if (chartRequestId) setViewMode("chart");
  }, [chartRequestId]);

  // Refresh affordance: the hook's `tick` keys the body's boundary, so a
  // refresh remounts the widget body and its queries re-run; the boundary
  // reports the remount, and the shared `useWidgetRefresh` state machine —
  // the same one behind the free toolbar's board-level control — watches
  // those queries settle before it claims success.
  const widgetRefresh = useWidgetRefresh();
  const refreshing = widgetRefresh.state === "refreshing";

  // The body's fill. The desktop dialog recesses it as a `sunken` well under
  // the tray; the phone drawer is one `background` surface end to end, so the
  // body paints the drawer's own fill and the table's opaque parts blend in.
  const bodySurface: SurfaceToken = isMobile ? "background" : "sunken";

  // "Dashboard / <widget>" trail. The dialog only opens from its own
  // dashboard view, so closing it returns the user there — satisfying both
  // "go to the dashboard" and "close if already there".
  const renderBreadcrumb = (current: WidgetInstance) => (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink
            render={<Box as="button" />}
            onClick={() => onOpenChange(false)}
          >
            Dashboard
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <EditableTitleField
            size="sm"
            weight="semibold"
            value={widgetTitle(current, registry, titleContext)}
            editable={canManage}
            onCommit={(next) => onConfigChange(current.id, { title: next })}
            aria-label="Widget name"
          />
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );

  // Prev/next nav — independent pill buttons on the painted header surface.
  const headerNav = (
    <Box direction="row" align="center" gap="xs" shrink={false}>
      <Button
        variant="outline"
        size="icon-sm"
        shape="pill"
        aria-label="Previous widget"
        disabled={!onPrevious}
        onClick={onPrevious}
      >
        <Icon name="chevron-left" size="sm" />
      </Button>
      <Button
        variant="outline"
        size="icon-sm"
        shape="pill"
        aria-label="Next widget"
        disabled={!onNext}
        onClick={onNext}
      >
        <Icon name="chevron-right" size="sm" />
      </Button>
    </Box>
  );

  // The header's action cluster: refresh, the view-mode switch and the widget
  // menu. The desktop dialog closes through its own button after these; the
  // drawer dismisses by swipe and backdrop, so it renders none.
  const renderHeaderActions = (current: WidgetInstance) => (
    <>
      {/* Refresh status label lives outside the action, to its left; the
          action itself is icon-only — matching the other icon-only header
          actions beside it. */}
      <WidgetRefreshStatus refresh={widgetRefresh} />
      {isMobile ? null : (
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Refresh widget"
          disabled={refreshing}
          onClick={() => {
            trackEvent({
              context: AnalyticsContext.WidgetView,
              event: WidgetViewEvent.RefreshWidget,
              data: { type: instance?.type },
            });
            widgetRefresh.refresh();
          }}
        >
          {/* At rest the glyph inherits the ghost Button's foreground so it
              matches the other header actions; the outcome states color it. */}
          <WidgetRefreshIcon refresh={widgetRefresh} size="sm" />
        </Button>
      )}
      {directDataFullscreen || drawerless ? null : (
        <Tooltip>
          <TooltipTrigger
            render={
              // The pressed wash marks non-default views; the icon says which
              // one.
              <Toggle
                size="sm"
                aria-label={viewModeControl.label}
                pressed={visibleViewMode !== "chart"}
                onPressedChange={() => {
                  trackEvent({
                    context: AnalyticsContext.WidgetView,
                    event: WidgetViewEvent.ToggleDetailView,
                    data: { mode: viewModeControl.next, type: instance?.type },
                  });
                  setViewMode(viewModeControl.next);
                }}
              />
            }
          >
            <Icon name={viewModeControl.icon} size="sm" />
          </TooltipTrigger>
          <TooltipPopup>{viewModeControl.label}</TooltipPopup>
        </Tooltip>
      )}
      {canManage || isMobile ? (
        <Menu>
          <MenuTrigger
            render={<Button variant="ghost" size="icon-sm" aria-label="Widget actions" />}
          >
            <Icon name="ellipsis" size="sm" />
          </MenuTrigger>
          <MenuPopup align="end">
            {isMobile ? (
              <MenuItem disabled={refreshing} onClick={widgetRefresh.refresh}>
                <WidgetRefreshIcon refresh={widgetRefresh} size="md" />
                <Text>{refreshing ? "Refreshing..." : "Refresh"}</Text>
              </MenuItem>
            ) : null}
            {canManage ? (
              <>
                {isMobile ? <MenuSeparator /> : null}
                <MenuItem onClick={() => onDuplicate(current)}>
                  <Icon name="copy" size="md" />
                  <Text>Duplicate</Text>
                </MenuItem>
                {onConvertToCustom && lockedKeys.size > 0 ? (
                  <MenuItem onClick={() => onConvertToCustom(current)}>
                    <Icon name="sliders-horizontal" size="md" />
                    <Text>Convert to custom</Text>
                  </MenuItem>
                ) : null}
                <MenuSeparator />
                <MenuItem
                  onClick={() => {
                    onDelete(current.id);
                    onOpenChange(false);
                  }}
                >
                  <Icon name="trash" size="md" color="muted" />
                  <Text>Delete</Text>
                </MenuItem>
              </>
            ) : null}
          </MenuPopup>
        </Menu>
      ) : null}
    </>
  );

  // The preview + drawer region. Everything in it sits on `bodySurface` — the
  // provider tells widget bodies and the data panel's table so their opaque
  // parts (sticky headers, pinned cells) paint that fill rather than cutting
  // a differently-colored hole.
  const renderBody = (current: WidgetInstance) => (
    <WidgetSurfaceProvider value={bodySurface}>
      {directDataFullscreen ? (
        <Box direction="column" fill="flex" overflow="hidden" paddingTop="xs">
          <WidgetRefreshBoundary
            key={`${current.id}:${widgetRefresh.tick}`}
            onMounted={widgetRefresh.onRemounted}
          >
            <WidgetDataPanel instance={current} slice={null} wide />
          </WidgetRefreshBoundary>
        </Box>
      ) : (
        <Box direction="row" fill="flex">
          {visibleViewMode !== "data" ? (
            <Box
              direction="column"
              fill="flex"
              padding={isMobile ? "md" : "lg"}
              gap="sm"
              overflow="hidden"
              align="center"
            >
              {/* Title spans the full panel width, left-aligned — decoupled
                  from the preview below so the input doesn't jump as the side
                  panel resizes or switches view modes. */}
              <Box direction="column" gap="2xs" shrink={false} align="start" fill="width">
                <EditableTitleField
                  fill
                  size="lg"
                  weight="bold"
                  align="left"
                  value={widgetTitle(current, registry, titleContext)}
                  editable={canManage}
                  onCommit={(next) => onConfigChange(current.id, { title: next })}
                  aria-label="Widget name"
                />
              </Box>
              {/* The widget body fills all the horizontal room, re-rendering
                  (and re-fetching) when the refresh key bumps. */}
              {/* `fill` is flex-1 + w-full, so the panel follows every drawer
                  resize instead of taking the chart's intrinsic width inside
                  this centered column. `direction="column"` is also
                  load-bearing: the widget body root claims its height with
                  `fill`, and flex-1 is inert in a block parent — a chart would
                  measure itself at 0 tall. */}
              <Box fill direction="column" rounded="xl" padding="md" overflow="hidden">
                <WidgetInstanceProvider instanceId={current.id} config={current.config ?? null}>
                  <WidgetDrilldownProvider value={inDialogDrilldown}>
                    <WidgetRefreshBoundary
                      key={`${current.id}:${widgetRefresh.tick}`}
                      onMounted={widgetRefresh.onRemounted}
                    >
                      <Widget
                        module={current.type}
                        config={current.config ?? undefined}
                        schemaVersion={current.schemaVersion}
                        w={12}
                        h={8}
                        expanded
                        onConfigChange={
                          canManage ? (patch) => onConfigChange(current.id, patch) : undefined
                        }
                      />
                    </WidgetRefreshBoundary>
                  </WidgetDrilldownProvider>
                </WidgetInstanceProvider>
              </Box>
            </Box>
          ) : null}

          {visibleViewMode !== "chart" ? (
            <>
              {/* Drawer: Settings / Data tabs over the matching panels. In
                  split mode, the left border divides it from the preview and
                  doubles as the drag seam; in data mode, it fills the full
                  body panel. */}
              <Box
                direction="column"
                fill={visibleViewMode === "data" ? true : undefined}
                width={visibleViewMode === "split" ? panelWidth : undefined}
                minWidth={visibleViewMode === "split" ? 320 : undefined}
                maxWidth={visibleViewMode === "split" ? panelMaxWidth : undefined}
                shrink={visibleViewMode === "split" ? false : undefined}
                background={bodySurface}
                border={visibleViewMode === "split" ? ["left"] : undefined}
                resize={visibleViewMode === "split" ? "left" : undefined}
                resizeLabel="Resize panel"
                onResize={({ width }) => setPanelWidth(width)}
              >
                <Tabs
                  value={hasDataTab ? (canManage ? panelTab : "data") : "settings"}
                  onValueChange={setPanelTab}
                >
                  {/* Underline tabs spanning the full drawer width — the list
                      stretches (`fullWidth`) and each tab already grows, so
                      the active bar reaches the edges. The strip rides a
                      hairline, the same masthead rule the team and workflow
                      shells seat under their view tabs: it sits flush so the
                      active tab's bar overlaps it — any padding between them
                      breaks the genre. */}
                  <Box shrink={false}>
                    <TabsList variant="underline" fullWidth>
                      {/* Non-managers get a Data-only drawer: Settings is an
                          editing surface, and on managed surfaces (My Work)
                          the toggle panel owns card management. */}
                      {canManage ? (
                        <TabsTab value="settings">
                          <Icon name="settings" size="sm" />
                          <Text>Settings</Text>
                        </TabsTab>
                      ) : null}
                      {hasDataTab ? (
                        <TabsTab value="data">
                          <Icon name="table-properties" size="sm" />
                          <Text>Data</Text>
                        </TabsTab>
                      ) : null}
                    </TabsList>
                    <Separator />
                  </Box>
                  {/* No wrapping scroller: TabsPanel is itself a flex column
                      with its own overflow, so the active tab fills the
                      drawer's remaining height. Settings scrolls within that;
                      the Data tab's DataTable fills it and scrolls internally
                      with its sticky header pinned. */}
                  {canManage ? (
                    <TabsPanel value="settings">
                      <WidgetSettingsPanel
                        instance={current}
                        canManage={canManage}
                        onConfigChange={onConfigChange}
                      />
                    </TabsPanel>
                  ) : null}
                  {hasDataTab ? (
                    <TabsPanel value="data">
                      <WidgetDataPanel
                        instance={current}
                        // The Data view alone takes the dialog's width; beside
                        // the chart it is a drawer.
                        wide={visibleViewMode === "data"}
                        slice={dataSlice ?? null}
                        onClearSlice={
                          onDataSliceChange ? () => onDataSliceChange(null) : undefined
                        }
                      />
                    </TabsPanel>
                  ) : null}
                </Tabs>
              </Box>
            </>
          ) : null}
        </Box>
      )}
    </WidgetSurfaceProvider>
  );

  if (isMobile) {
    // Phones present the editor as a bottom drawer on the page fill — the
    // mobile shape of every detail dialog. The header row keeps the trail,
    // the nav and the actions; the drawer's bar and backdrop are the close.
    // The panel is the `workspace` twin of the dialog's body: the chart
    // column and the Data table own their own scrolling, so the panel adds
    // no scroller of its own. A project opened from the Data tab presents
    // over this drawer, and the host marks the subtree so the open pushes
    // onto the detail stack instead of clearing it.
    return (
      <Drawer open={open} onOpenChange={onOpenChange} size="full">
        <DrawerPopup showBar surface="background">
          {instance && (
            <DetailStackHost key={instance.id}>
              <DrawerTitle visuallyHidden>{widgetTitle(instance, registry, titleContext)}</DrawerTitle>
              <DrawerHeader>
                <Box direction="row" align="center" gap="sm">
                  <Box fill="flex" minWidth={0} overflow="hidden">
                    {renderBreadcrumb(instance)}
                  </Box>
                  {headerNav}
                  <Box direction="row" align="center" gap="xs" shrink={false}>
                    {renderHeaderActions(instance)}
                  </Box>
                </Box>
              </DrawerHeader>
              <DrawerPanel variant="workspace">{renderBody(instance)}</DrawerPanel>
            </DetailStackHost>
          )}
          {/* App-level dialogs opened from this sheet stack on it. */}
          <DialogHost />
        </DrawerPopup>
      </Drawer>
    );
  }

  return (
    // Not responsive: phones render the Drawer branch above.
    <Dialog responsive={false} open={open} onOpenChange={onOpenChange}>
      {/* Match the project dialog geometry so the nested project changes modal
        * depth without changing the surface dimensions. `p-0` lets the frame
        * meet the popup edges. The header owns the close button. */}
      <IssueViewDialogBoundaryPopup
        className={`${FULLSCREEN_DIALOG_POPUP_CLASS} p-0`}
        ownerOpen={open}
        showCloseButton={false}
      >
        {instance && (
          <Frame fill="flex" key={instance.id}>
            <DialogTitle visuallyHidden>
              {widgetTitle(instance, registry, titleContext)}
            </DialogTitle>

            {/* Header: nav · centered breadcrumb · actions — sits directly on
                the tray, outside the panel. The breadcrumb is absolutely
                centered across the full header width (so it stays centered
                regardless of the differing nav/action widths); a flex spacer
                keeps nav left and actions right. */}
            <FrameHeader
              direction="row"
              align="center"
              gap="sm"
              shrink={false}
              minHeight={48}
              paddingX="xs"
              paddingY="none"
              position="relative"
            >
              {/* The trail, centered behind the controls. The overlay is inert
                  to the pointer so the empty area passes clicks through to the
                  nav/action buttons, while the centered breadcrumb re-arms its
                  own pointer events and stays clickable to edit — a
                  pass-through overlay needs both layers. */}
              <Box
                pointerEvents="none"
                position="absolute"
                left={0}
                right={0}
                top={0}
                bottom={0}
                align="center"
                justify="center"
              >
                <Box pointerEvents="auto">{renderBreadcrumb(instance)}</Box>
              </Box>

              {headerNav}

              <Box fill="flex" />

              <Box direction="row" align="center" gap="xs" shrink={false}>
                {renderHeaderActions(instance)}
                <Button
                  aria-label="Close"
                  size="icon-sm"
                  color="neutral"
                  onClick={() => onOpenChange(false)}
                >
                  <Icon name="x" />
                </Button>
              </Box>
            </FrameHeader>

            {/* Body panel: one FramePanel holding the whole preview + drawer
                region — a `sunken` well, since the tray floats at `popover`.
                Its border outlines the region; the Settings/Data drawer adds
                only a left divider inside it. */}
            <FramePanel
              ref={bodyRef}
              fill="flex"
              overflow="hidden"
              padding="none"
              surface={bodySurface}
            >
              {renderBody(instance)}
            </FramePanel>
          </Frame>
        )}
      </IssueViewDialogBoundaryPopup>
    </Dialog>
  );
}
